import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdvertisementList from "@/components/advertisements/AdvertisementList";
import { AdvertisementFilters } from "@/services/AdvertisementService";

const AdvertisementPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [filters, setFilters] = useState<AdvertisementFilters>({
    active: true,
    type: null,
    page: null,
  });

  // Get params from URL when component mounts or URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get("searchval");
    const pageFromUrl = searchParams.get("page");
    const limitFromUrl = searchParams.get("limit");
    const activeFromUrl = searchParams.get("active");
    const typeFromUrl = searchParams.get("type");
    const pageFilterFromUrl = searchParams.get("pageFilter");

    if (searchFromUrl) {
      setSearchValue(searchFromUrl);
    } else {
      setSearchValue("");
    }

    if (pageFromUrl && !isNaN(parseInt(pageFromUrl))) {
      setCurrentPage(parseInt(pageFromUrl));
    } else {
      setCurrentPage(1);
    }

    // Valid limit values: 5, 10, 20, 50
    const validLimits = [5, 10, 20, 50];
    if (limitFromUrl && !isNaN(parseInt(limitFromUrl))) {
      const parsedLimit = parseInt(limitFromUrl);
      if (validLimits.includes(parsedLimit)) {
        setCurrentLimit(parsedLimit);
      }
    }

    // Parse filters
    setFilters({
      active: activeFromUrl === null ? true : activeFromUrl === "true",
      type: typeFromUrl as "ad_photo" | "ad_vid" | null,
      page: pageFilterFromUrl as "profile" | "session" | "both" | null,
    });
  }, [location.search]);

  // Update URL when search changes
  const handleSearchChange = (search: string) => {
    setSearchValue(search);
    setCurrentPage(1);
    updateUrlParams({ searchval: search || null, page: null });
  };

  // Update URL when page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlParams({ page: page > 1 ? page.toString() : null });
  };

  // Update URL when limit changes
  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(1);
    updateUrlParams({
      limit: limit !== 10 ? limit.toString() : null,
      page: null,
    });
  };

  // Update URL when filters change
  const handleFiltersChange = (newFilters: AdvertisementFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    updateUrlParams({
      active: newFilters.active !== true && newFilters.active !== null && newFilters.active !== undefined ? newFilters.active.toString() : null,
      type: newFilters.type || null,
      pageFilter: newFilters.page || null,
      page: null,
    });
  };

  // Helper to update URL params
  const updateUrlParams = (params: { [key: string]: string | null }) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === "") {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <AdvertisementList
        initialSearch={searchValue}
        onSearchChange={handleSearchChange}
        initialPage={currentPage}
        onPageChange={handlePageChange}
        initialLimit={currentLimit}
        onLimitChange={handleLimitChange}
        initialFilters={filters}
        onFiltersChange={handleFiltersChange}
      />
    </div>
  );
};

export default AdvertisementPage;